import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hexagon, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useBranding } from "../contexts/BrandingContext";
import { authApi, formatApiError } from "../lib/ledgerApi";

export default function Signup() {
  const { setSession } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();

  const [form, setForm] = useState({
    legal_name: "",
    vpa_handle: "",
    email: "",
    password: "",
    country: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const handle = form.vpa_handle.toLowerCase().trim();
      const data = await authApi.signup({ ...form, vpa_handle: handle });
      setSession(data);
      nav("/app/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-foreground">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-border bg-card grid-line-bg">
        <Link to="/" className="flex items-center gap-2 font-medium">
          <span className="w-8 h-8 inline-flex items-center justify-center border border-foreground/40 rounded-sm">
            <Hexagon className="w-4 h-4" strokeWidth={1.5} />
          </span>
          <span>{branding.product_name}</span>
        </Link>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Create business</div>
          <h1 className="text-4xl tracking-tighter font-medium leading-[1.05]">
            Spin up wallets across 7 currencies in 30 seconds.
          </h1>
          <p className="mt-4 text-sm text-muted-foreground max-w-md leading-relaxed">
            We give you a VPA like <code className="font-mono text-foreground">acme@{branding.brand_domain}</code> and one ledger address per currency.
            Counterparties can pay you instantly — no bank rails.
          </p>
        </div>
        <div className="text-xs font-mono text-muted-foreground">Phase 1 · Core Payment Rail</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">/ New business</div>
            <h2 className="text-3xl tracking-tight font-medium">Create account</h2>
          </div>

          {error && (
            <div data-testid="signup-error" className="border border-status-tampered/40 bg-status-tampered/10 p-3 rounded-sm text-status-tampered text-xs font-mono">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Legal name</Label>
            <Input required data-testid="signup-legal-name" value={form.legal_name} onChange={set("legal_name")} className="rounded-sm" placeholder="Acme Corp" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">VPA handle</Label>
            <div className="flex items-center gap-2">
              <Input
                required
                data-testid="signup-vpa-handle"
                value={form.vpa_handle}
                onChange={(e) => setForm((f) => ({ ...f, vpa_handle: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") }))}
                className="rounded-sm font-mono"
                placeholder="acme"
              />
              <span className="text-xs font-mono text-muted-foreground">@{branding.brand_domain}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Admin email</Label>
            <Input type="email" required data-testid="signup-email" value={form.email} onChange={set("email")} className="rounded-sm font-mono" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
            <Input type="password" required minLength={6} data-testid="signup-password" value={form.password} onChange={set("password")} className="rounded-sm font-mono" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Country (optional)</Label>
            <Input data-testid="signup-country" value={form.country} onChange={set("country")} className="rounded-sm" placeholder="US" />
          </div>

          <Button type="submit" disabled={busy} data-testid="signup-submit" className="rounded-sm w-full">
            {busy ? "Creating…" : (<span className="inline-flex items-center gap-2">Create business <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></span>)}
          </Button>

          <div className="text-xs text-muted-foreground">
            Have an account?{" "}
            <Link to="/login" data-testid="link-login" className="underline hover:text-foreground">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
